import { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { injectGlobal } from '@emotion/css';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { PhraseContent } from 'views/Setting/Phrase/PhraseContent';

import { useOrgValue } from 'hooks/business/system/useOrg';
import { useRouteAccessProps } from 'hooks/business/useNewPermisson';
import { serviceAccess } from 'views/Management/router';
import { hidePayment } from 'services/chattt';
import {
  route as basisRoute,
  phraseRouteAccess,
  remindRouteAccess,
  todoRouteAccess,
  pauseAccess,
  phraseRoute,
  welcomeReplyRoute,
  timeoutReplyRoute,
} from './Basis';
import { route as qaRoute } from './Qa';
import { route as orderRoute } from './Order';
import { route as payRoute } from './Pay';
import { route as logisticsRoute } from './Logistics';
import { route as reviewRoute } from './Review';
import { route as saveOrderRoute } from './SaveOrder';
import { outstockRouteAccess, route as outStockRoute } from './OutStock';
import { route as csatRoute, csatRouteAccess } from './Csat';
import RemindTask from './Basis/Remind/Task';
import PauseTask from './Basis/Pause/Task';
import TodoTaskPolicy from './Basis/Todo/TaskPolicy';
import Entry from './Entry';
import useEntryTrack from './hooks/useEntryTrack';
import { AideContextProvider } from './Context';
import Task from './Scenario/Task';
import Policy from './Scenario/Policy';
import { KnowledgeContextProvider } from '@knowledge/knowledges/general/KnowledgeContext';
import { isMasterOnline, isQuickChat, isSiteGiant } from 'productLine';
import { getStoreCount } from 'components/StoreScopes';
import Forbidden from './Forbidden';

import OutStockTask from './OutStock/Task';
import RemindDashboard from 'views/Setting/Remind/Dashboard';
import { ReactScrollProvider } from './components/ReactScrollContext';
import TemplateEdit from './Whatsapp/TemplateEdit';
import CsatReply from './Csat/CsatReply';

injectGlobal`
  html {
    --theme-background-color: #E9F3FF;
    --theme-remind-color: #FFAB00;
    --theme-fill-1: #464D5A;
    --theme-fill-2: #81868F;
    --theme-fill-3: #CBCDD1;
    --theme-fill-4: #E9EBED;
    --theme-fill-5: #F3F5F7;
    --theme-fill-6: #F9FAFB;
    --theme-fill-7: #FFFFFF;
    --theme-text-1: #1D2129;
    --theme-text-2: #4E5969;
    --theme-text-3: #86909C;
    --theme-text-4: #86909C;
    --theme-border-1: #F0F2F5;
    --theme-border-2: #E1E4EB;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > * {
    flex: 1 0 0;
  }
  > .${({ theme }) => theme.getAntdPrefixedClassName('alert')} {
    flex: 0 0 auto;
    button {
      height: 18px;
      padding: 0 8px;
    }
  }
`;

const EditContainer = styled.div`
  height: 100%;
  padding: 16px;
  overflow: auto;
`;

const phraseContentRoute = {
  path: 'phrase/:platform/:action/:id?',
  redirect: true,
  i18nHelmetTitle: 'phrase.快捷回复',
  element: <PhraseContent />,
  access: phraseRouteAccess,
};

export const scenarioRoutes = isQuickChat
  ? [
      phraseRoute,
      welcomeReplyRoute,
      timeoutReplyRoute,
      orderRoute,
      payRoute,
      logisticsRoute,
      saveOrderRoute,
      csatRoute,
      reviewRoute,
    ]
  : [
      basisRoute,
      qaRoute,
      orderRoute,
      payRoute,
      outStockRoute,
      logisticsRoute,
      saveOrderRoute,
      csatRoute,
      reviewRoute,
    ];

export const childRoutes = [
  phraseContentRoute,
  {
    path: 'remind/dashboard',
    i18nHelmetTitle: 'remind.数据统计',
    element: <RemindDashboard />,
    access: remindRouteAccess,
  },
  {
    path: 'remind/task/:action/:id?',
    i18nHelmetTitle: 'nav.remind',
    element: (
      <EditContainer>
        <RemindTask />
      </EditContainer>
    ),
    access: remindRouteAccess,
  },
  {
    path: 'pause/task/:action/:id?',
    i18nHelmetTitle: 'pause_auto_reply',
    element: (
      <EditContainer>
        <PauseTask />
      </EditContainer>
    ),
    access: pauseAccess,
  },
  {
    path: 'todo/taskPolicy/:action/:id?',
    i18nHelmetTitle: 'todo.待办',
    element: (
      <ReactScrollProvider>
        <EditContainer>
          <TodoTaskPolicy />
        </EditContainer>
      </ReactScrollProvider>
    ),
    access: todoRouteAccess,
  },
  {
    path: 'whatsapp/template/:action/:name?',
    i18nHelmetTitle: 'nav.waMsgTemp',
    element: (
      <ReactScrollProvider>
        <EditContainer>
          <TemplateEdit />
        </EditContainer>
      </ReactScrollProvider>
    ),
  },
  {
    path: 'outstock/remind/task/:platform/:action/:id?',
    i18nHelmetTitle: 'outofstock_manage',
    element: (
      <EditContainer>
        <OutStockTask scenarioType="OUT_STOCK_REMINDER" />
      </EditContainer>
    ),
    access: outstockRouteAccess,
  },
  {
    path: 'outstock/follow/task/:platform/:action/:id?',
    i18nHelmetTitle: '问题订单跟进',
    element: (
      <EditContainer>
        <OutStockTask scenarioType="ORDER_FOLLOWUP" />
      </EditContainer>
    ),
    access: outstockRouteAccess,
  },
  {
    path: 'csat/reply/:platform/:path/:type',
    i18nHelmetTitle: '编辑-邀好评话术',
    element: (
      <ReactScrollProvider>
        <EditContainer>
          <CsatReply />
        </EditContainer>
      </ReactScrollProvider>
    ),
    access: csatRouteAccess,
  },
  {
    path: 'task/:scenarioType/:platform/:region/:action/:id?',
    i18nHelmetTitle: 'marketing.任务',
    element: (
      <EditContainer>
        <Task />
      </EditContainer>
    ),
  },
  {
    path: 'scenario/policy/:scenarioType/:platform/:region/:round/:replyOption/:action/:id?',
    element: (
      <ReactScrollProvider>
        <EditContainer>
          <Policy />
        </EditContainer>
      </ReactScrollProvider>
    ),
  },
  ...scenarioRoutes,
];

export interface AideProps {
  allowEntry?: boolean;
}

export function Aide(props: AideProps) {
  const { allowEntry = true } = props;

  const { t } = useTranslate();

  const { pathname } = useLocation();

  const nav = useNavigate();

  const orgValue = useOrgValue();
  const expiredInDays = orgValue?.expiredInDays;

  useEntryTrack('aide');

  const routeAccessProps = useRouteAccessProps();

  const hasServiceAccess = serviceAccess(routeAccessProps);

  const showExpireTips = expiredInDays && expiredInDays <= 5 && hasServiceAccess;

  const alert = showExpireTips ? (
    <Alert
      banner
      message={
        <div>
          {t('marketing.当前套餐将在x天后到期，到期后跟单助手中的功能将会停止使用，请及时订购~', {
            x: expiredInDays < 0 ? 0 : expiredInDays,
          })}
          {!hidePayment && !isMasterOnline && !isSiteGiant && (
            <Button type="link" onClick={() => nav('/management/service/market')}>
              {t('marketing.去订购')}
              <RightOutlined />
            </Button>
          )}
        </div>
      }
    />
  ) : null;

  const withContainer = (...nodes: ReactNode[]) => {
    return (
      <KnowledgeContextProvider>
        <Container>
          {alert}
          {nodes}
        </Container>
      </KnowledgeContextProvider>
    );
  };

  if (pathname !== '/aide') {
    if (
      routeAccessProps.newPermissionMap['快捷回复-个人模版']?.length &&
      getStoreCount(routeAccessProps.newPermissionMap['快捷回复-个人模版']) === 0
    ) {
      return <Forbidden />;
    }

    return (
      <AideContextProvider value={{ allowEntry }}>{withContainer(<Outlet />)}</AideContextProvider>
    );
  }

  if (allowEntry) {
    return withContainer(<Entry />);
  }

  return <Navigate replace to="/404" />;
}

export default Aide;
